/**
 * OrgKontekstBanner
 * Viser superadmin hvilken organisasjon handlingene gjelder, og lar superadmin velge/bytte
 * organisasjon fra en søkbar liste. Se Docs/SUPERADMIN_ORG_KONTEKST_PLAN.md.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { superadminAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

const OrgKontekstBanner: React.FC = () => {
  const { hasRole, orgKontekst, avsluttOrgKontekst, velgOrgKontekst } = useAuth();
  const navigate = useNavigate();
  const [apen, setApen] = React.useState(false);
  const [velger, setVelger] = React.useState(false);
  const [feil, setFeil] = React.useState<string | null>(null);

  const erSuperadmin = hasRole('superadmin');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['superadmin', 'organisasjoner'],
    queryFn: () => superadminAPI.getOrganisasjoner(),
    enabled: erSuperadmin && apen,
  });

  if (!erSuperadmin) return null;

  const organisasjoner = data?.organisasjoner ?? [];

  const avslutt = async () => {
    await avsluttOrgKontekst();
    navigate('/superadmin');
  };

  const velg = async (id: number) => {
    setApen(false);
    setFeil(null);
    setVelger(true);
    try {
      await velgOrgKontekst(id);
      navigate('/dashboard');
    } catch (err: any) {
      setFeil(err?.response?.data?.error || 'Kunne ikke velge organisasjonen.');
    } finally {
      setVelger(false);
    }
  };

  return (
    <div role="status" className="w-full bg-amber-400 text-amber-950">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2 text-sm font-medium">
        <span className="flex min-w-0 items-center gap-2">
          <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0">
            {orgKontekst ? (
              <>
                Du jobber i: <strong>{orgKontekst.navn}</strong>
                {!orgKontekst.isActive && ' (inaktiv)'}
              </>
            ) : (
              'Velg organisasjon for å jobbe i den'
            )}
          </span>
        </span>
        <span className="flex gap-2">
          <Popover open={apen} onOpenChange={setApen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant={orgKontekst ? 'secondary' : 'default'}
                disabled={velger}
                aria-haspopup="listbox"
                aria-expanded={apen}
              >
                {orgKontekst ? 'Bytt organisasjon' : 'Velg organisasjon'}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-70" aria-hidden="true" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[min(20rem,calc(100vw-2rem))] p-0">
              <Command>
                <CommandInput placeholder="Søk etter organisasjon …" />
                <CommandList>
                  {isLoading ? (
                    <div className="py-6 text-center text-sm">Henter organisasjoner …</div>
                  ) : isError ? (
                    <div className="py-6 text-center text-sm">Kunne ikke hente organisasjoner.</div>
                  ) : (
                    <>
                      <CommandEmpty>Ingen organisasjoner</CommandEmpty>
                      <CommandGroup>
                        {organisasjoner.map((org) => (
                          <CommandItem key={org.id} value={`${org.navn} ${org.id}`} onSelect={() => velg(org.id)}>
                            <span className="flex-1">
                              <span>{org.navn}</span>
                              {!org.isActive && <span> (inaktiv)</span>}
                            </span>
                            {orgKontekst?.id === org.id && (
                              <Check data-testid="gjeldende-org" className="ml-2 h-4 w-4" aria-label="Gjeldende" />
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {orgKontekst && (
            <Button size="sm" variant="secondary" onClick={avslutt} disabled={velger}>
              Avslutt
            </Button>
          )}
        </span>
      </div>
      {feil && (
        <div role="alert" className="mx-auto max-w-6xl px-4 pb-2 text-sm font-semibold text-red-900">
          {feil}
        </div>
      )}
    </div>
  );
};

export default OrgKontekstBanner;
